// PARK, DON'T FAIL (2026-09-24): a coding task that exhausts its attempt ladder with work
// saved on its own branch waits and resumes from that work instead of ending `failed`.
//
// THE FAILURE IT REPLACES. A large team-lane task gets most of the way, saves its diff, and
// spends its last attempt on the final gate failures; the task ends `failed` and a human
// redispatches it by hand — tesote plan 101 was redispatched ~20 times in one day. The owner's
// ruling: "it should wait more and multiple retries" — so the ladder's end is a PARK on
// {@link TaskHoldReason.ResumeBackoff}, and the task comes back on its own after a backoff with
// a fresh, small attempt window, resuming the saved diff (the box's ordinary restore,
// `apps/runner/src/modes/worker/team/session/resume.ts`).
//
// WHAT STOPS IT, by enumeration — every bound names its failure:
//   - NO PROGRESS: {@link RESUME_NO_PROGRESS_LIMIT} consecutive cycles whose saved gate count
//     did not drop below the best one seen end the task (`resume_stalled`). Without it a task
//     the model cannot finish re-runs a paid session every few hours forever. A cycle that
//     gets closer resets the count.
//   - CEILING: {@link RESUME_MAX_CYCLES} parks, or {@link RESUME_MAX_ELAPSED_MS} since the
//     first park, end it (`resume_exhausted`) — the bound on a task that keeps shaving one
//     failure off a suite that never goes green.
//   - IDENTICAL RED (2026-09-28): a save whose resumed gate named the SAME failure set it
//     resumed (`gate_progress.repeated`) jumps the no-progress count to one short of the
//     limit, so the task gets exactly ONE more cycle — the runner makes it fix-focused — and
//     a second identical red ends it `gate_repeated`, which the worker escalates as a split
//     ask or an owner blocker (#5293's `ci_not_converging` shape: one round, not zero). The
//     measured cost it stops: `tsk_79e812998edec893d5b06f02dcc7f3a9` (tesote/tesote.ai)
//     resumed 3 -> 3 six times on 2026-09-26, each a full paid team session. A cycle after
//     the repeat that is merely not better still ends `resume_stalled`, as any stall does.
// Ending is the ONE escalation: the terminal write is audited visible to the maintainer and
// fires the task-lifecycle notification, with the reason naming which bound ended it.
//
// WHAT PARKS: an exhausted ladder of a WORK kind ({@link RESUME_PARKABLE_KINDS}) on a task with
// saved work. Box, network and provider kinds keep their own exhaustion (`fail`);
// a task with nothing saved has nothing to resume. A manual `task redispatch` still works on a
// parked task and resumes it at once (`redispatchTask`'s `freshBudget`).

import { projectFailureKinds, type TaskFailureKind, TaskFailureKindSchema } from './failure-kind';

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;

/**
 * The wait before resume cycle N (0-based); past the end, the last entry repeats. Patient on
 * purpose (owner, 2026-09-24): a red tail often needs the fleet, the provider or the base
 * branch to move before another attempt can pass, and a short wait re-runs the same run.
 */
export const RESUME_BACKOFF_MS: readonly number[] = [
  10 * MINUTE_MS,
  30 * MINUTE_MS,
  HOUR_MS,
  2 * HOUR_MS,
  4 * HOUR_MS,
];

/** Charged attempts each resume cycle may spend before the ladder parks (or ends) again. */
export const RESUME_ATTEMPTS_PER_CYCLE = 1;

/** Consecutive cycles with no drop in the saved gate count that end the task. */
export const RESUME_NO_PROGRESS_LIMIT = 3;

/** Total parks a task may take before it ends, progress or not. */
export const RESUME_MAX_CYCLES = 12;

/** Wall-clock bound from the first park; past it the next exhaustion ends the task. */
export const RESUME_MAX_ELAPSED_MS = 48 * HOUR_MS;

const RESUME_PARKABLE = projectFailureKinds((row) => row.resumeParkable);

/** The failure kinds whose exhausted ladder parks instead of failing — the work's own endings (each row's `resumeParkable`). */
export const RESUME_PARKABLE_KINDS: ReadonlySet<TaskFailureKind> = new Set(
  TaskFailureKindSchema.options.filter((kind) => RESUME_PARKABLE[kind]),
);

/** The park state a task carries between cycles (`tasks.resume_*`). */
export type ResumeState = {
  /** Parks taken so far. */
  readonly cycles: number;
  /** Consecutive cycles without progress. */
  readonly stalls: number;
  /** The lowest gate-failure count any saved attempt reached; `null` before one was read. */
  readonly bestFailures: number | null;
  /** When the first park happened; `null` before it. */
  readonly startedAt: Date | null;
};

/** What the latest saved WIP says about the attempt that saved it. */
export type ResumeEvidence = {
  /** `gate_red` | `guard_blocked` | `interrupted` | `gate_refused` — only `gate_red` carries a comparable count. */
  readonly ending: string;
  readonly failures: number;
  /**
   * The attempt that saved it resumed a save and its gate named the IDENTICAL failure set
   * (`gate_progress.repeated` on `team_wip_saved`). Absent/false ⇒ an ordinary save.
   */
  readonly repeated?: boolean;
  /**
   * THIS CYCLE's red moved to a different gate stage than the save it resumed
   * (`gate_progress.stage` on the failure write that ended the cycle, `taskFailureGateStage`).
   * Read off the cycle's own failure, never off a save row, so a cycle is graded once:
   * re-reading one advanced save on the next cycle does not buy progress twice. Absent ⇒
   * the counts compare.
   */
  readonly stage?: 'advanced' | 'regressed';
};

/** Why a resume bound ended the task — each arm is one bound in the module header. */
export type ResumeEndReason = 'resume_stalled' | 'resume_exhausted' | 'gate_repeated';

export type ResumeVerdict =
  | {
      readonly kind: 'park';
      readonly holdMs: number;
      readonly next: ResumeState;
      /** Did this cycle's saved count drop below the best one? */
      readonly progressed: boolean;
    }
  | { readonly kind: 'end'; readonly reason: ResumeEndReason };

/**
 * The stall count for an ORDINARY (non-repeated) save: the first park is the baseline and
 * never a stall; progress resets; anything else counts. An identical red never reaches this
 * function — it jumps to one short of the limit even on the first park (see `resumeVerdict`).
 */
function ordinaryStalls(state: ResumeState, progressed: boolean): number {
  return progressed || state.cycles === 0 ? 0 : state.stalls + 1;
}

/**
 * PURE: park or end, for a task whose ladder just ran out with `evidence` saved. Progress is
 * a `gate_red` save whose count is below the best seen, or one whose cycle ADVANCED a gate
 * stage (`evidence.stage`), which resets the best to its count; the first park of an ordinary
 * save is the baseline and never a stall. Any other ending (a guard block, an interruption)
 * said nothing comparable, so it counts toward the no-progress limit. An IDENTICAL red
 * (`evidence.repeated`) is never progress, whatever its count, and leaves at most one more
 * cycle (see the module header) — INCLUDING on the first park: the repeat is measured
 * against the save the attempt resumed, not against a prior park, so it is evidence of a
 * stall the baseline rule has no reason to forgive (tsk_79e81299…: 3 -> 3 twice ends at the
 * second repeat).
 */
export function resumeVerdict(
  state: ResumeState,
  evidence: ResumeEvidence,
  now: Date,
): ResumeVerdict {
  const startedAt = state.startedAt ?? now;
  if (
    state.cycles >= RESUME_MAX_CYCLES ||
    now.getTime() - startedAt.getTime() >= RESUME_MAX_ELAPSED_MS
  ) {
    return { kind: 'end', reason: 'resume_exhausted' };
  }
  const repeated = evidence.repeated === true;
  const counted = evidence.ending === 'gate_red' && evidence.failures > 0;
  // A STAGE MOVE outranks the count (2026-09-30, tesote/tesote.ai `tsk_98ceab62…`): a cycle
  // that cleared typecheck and is now red on 6 lint failures got further than a best of 2
  // typecheck summary lines, and its count becomes the new best — the next cycle is graded
  // on the stage it reached. A cycle that fell back a stage never progressed.
  const advanced = evidence.stage === 'advanced';
  const progressed =
    !repeated &&
    counted &&
    evidence.stage !== 'regressed' &&
    (advanced || state.bestFailures === null || evidence.failures < state.bestFailures);
  const stalls = repeated
    ? Math.max(state.stalls + 1, RESUME_NO_PROGRESS_LIMIT - 1)
    : ordinaryStalls(state, progressed);
  if (stalls >= RESUME_NO_PROGRESS_LIMIT) {
    return { kind: 'end', reason: repeated ? 'gate_repeated' : 'resume_stalled' };
  }
  const bestFailures = progressed ? evidence.failures : state.bestFailures;
  const holdMs =
    RESUME_BACKOFF_MS[Math.min(state.cycles, RESUME_BACKOFF_MS.length - 1)] ?? 4 * HOUR_MS;
  return {
    kind: 'park',
    holdMs,
    progressed,
    next: { cycles: state.cycles + 1, stalls, bestFailures, startedAt },
  };
}

/**
 * PURE: the baseline a task's FIRST park grades against — the lowest `gate_red` count any
 * save BEFORE the latest one reached (`saves` oldest first, the latest last), or `null`.
 *
 * WHY. {@link ResumeState.bestFailures} is documented as "the lowest gate-failure count any
 * saved attempt reached", but the park only ever read the LATEST save, so a ladder that went
 * 7 → 2 → 17 before its first park baselined at 17 (tsk_4b4aadc3cbca8600d03236d795ffceb7,
 * tesote/tesote.ai, measured 2026-09-27: saves 7, 2, 17, then 2 on the first resume). Graded
 * against 17, every later count below it reads as progress, so the no-progress bound could
 * not see that the task had already been at 2 and regressed. The latest save is excluded
 * because it is the evidence being graded — it cannot also be its own baseline.
 */
export function resumeBaselineFailures(saves: readonly ResumeEvidence[]): number | null {
  const counted = saves
    .slice(0, -1)
    .filter((s) => s.ending === 'gate_red' && s.failures > 0)
    .map((s) => s.failures);
  return counted.length === 0 ? null : Math.min(...counted);
}

/** One parked task, as the stall report names it. */
export type ResumeBackoffView = {
  readonly taskId: string;
  readonly repoFullName: string;
  readonly nextResumeAt: Date;
  readonly cycles: number;
  readonly stalls: number;
  /** The gate-failure count the latest saved attempt ended on. */
  readonly lastFailures: number | null;
};

/** Parked tasks listed in the remedy before it degrades to a count. */
const RESUME_REMEDY_MAX = 10;

const minutes = (ms: number): string => `${String(ms / MINUTE_MS)}m`;

/** WHAT CLEARS {@link TaskHoldReason.ResumeBackoff}: the clock, or an operator redispatch. */
export function resumeBackoffRemedy(parked: readonly ResumeBackoffView[]): string {
  const how = `this task spent its attempts but saved its work as the tip commit of the task's branch (\`dz/<slug>--<first 8 hex of the task id>\`), so instead of failing it waits and resumes from that work on its own (backoff ${RESUME_BACKOFF_MS.map(minutes).join(' → ')}, the last one repeating). It ends — and says so — after ${String(RESUME_NO_PROGRESS_LIMIT)} resumes in a row that do not lower the gate's failure count, after a second resume whose gate names the identical failures it resumed (\`gate_repeated\`, escalated as a split ask or a blocker), or ${String(RESUME_MAX_CYCLES)} resumes / ${String(RESUME_MAX_ELAPSED_MS / HOUR_MS)}h in all. Nothing to do; \`task redispatch\` resumes it now`;
  if (parked.length === 0) return how;
  const named = parked
    .slice(0, RESUME_REMEDY_MAX)
    .map(
      (p) =>
        `${p.taskId} (${p.repoFullName}): resume ${String(p.cycles)}, next at ${p.nextResumeAt.toISOString()}, last gate failures ${p.lastFailures === null ? 'unknown' : String(p.lastFailures)}, ${String(p.stalls)} without progress`,
    );
  const more = parked.length - named.length;
  return `${how}. Parked now — ${named.join('; ')}${more > 0 ? `; and ${String(more)} more` : ''}`;
}
